export interface EvidenceOwnerCandidate { id: string; name: string }

const FILENAME_DESCRIPTOR_TOKENS = new Set(['account', 'accounts', 'annual', 'april', 'august', 'bad', 'bank',
  'business', 'cit', 'client', 'copy', 'csv', 'current', 'customer', 'data', 'december', 'deduction', 'demo',
  'document', 'draft', 'eight', 'evidence', 'expense', 'export', 'february', 'file', 'final', 'first', 'five',
  'four', 'freelance', 'good', 'import', 'income', 'invoice', 'january', 'json', 'july', 'june', 'ledger',
  'march', 'may', 'monthly', 'new', 'nine', 'notes', 'november', 'october', 'office', 'old', 'one', 'original',
  'other', 'paye', 'payment', 'payments', 'payroll', 'pdf', 'pension', 'personal', 'pit', 'png', 'processed',
  'raw', 'receipt', 'records', 'return', 'review', 'reviewed', 'salary', 'sales', 'sample', 'saved', 'scan',
  'scanned', 'second', 'september', 'seven', 'six', 'source', 'statement', 'supplies', 'tax', 'ten', 'test',
  'third', 'three', 'transaction', 'transactions', 'two', 'updated', 'upload', 'uploaded', 'vat', 'wages', 'wht',
  'xlsx', 'year']);

export type EvidenceOwnershipCheck =
  | { state: 'matched' | 'confirmed' | 'unverified'; detectedName?: string;
    detectedBy?: 'structured-owner-field' | 'known-name' | 'source-filename' }
  | { state: 'mismatch'; detectedName: string;
    detectedBy: 'structured-owner-field' | 'known-name' | 'source-filename' };

export interface ExtractedOwnershipCheck {
  state: 'matched' | 'unverified' | 'mismatch';
  detectedName?: string;
  taxIdentifier?: string;
  confidence?: string;
  basis?: string;
  extractor: string;
}

/**
 * A conservative pre-processing guard. Explicit owner fields in CSV/JSON and
 * names of authorised subjects are checked; binary sources remain subject to
 * document-extraction ownership checks and human confirmation.
 */
export async function detectEvidenceOwner(file: File, selected: EvidenceOwnerCandidate,
  authorisedSubjects: readonly EvidenceOwnerCandidate[] = [selected]): Promise<EvidenceOwnershipCheck> {
  let ownerValues: string[] = [];
  if (/^(text\/|application\/(json|csv))/.test(file.type) || /\.(csv|json|txt)$/i.test(file.name)) {
    try { ownerValues = explicitOwnershipValues(await file.text(), file.name); }
    catch { /* Filename checks remain available when a browser cannot read the file. */ }
  }
  const selectedTokens = nameTokens(selected.name);
  if (ownerValues.length > 0) {
    const selectedMatched = ownerValues.some((value) => tokensMatch(value, selectedTokens));
    if (selectedMatched) return { state: 'matched', detectedName: selected.name };
    const known = authorisedSubjects.find((subject) => subject.id !== selected.id
      && ownerValues.some((value) => tokensMatch(value, nameTokens(subject.name))));
    return { state: 'mismatch', detectedName: known?.name ?? ownerValues[0]!, detectedBy: 'structured-owner-field' };
  }
  return detectEvidenceOwnerFromFilename(file.name, selected, authorisedSubjects);
}

/** Conservative filename fallback shared by live intake and session recovery.
 * It produces a candidate for review; the server remains responsible for the
 * durable assessment and the filename is never identity authority. */
export function detectEvidenceOwnerFromFilename(filenameValue: string, selected: EvidenceOwnerCandidate,
  authorisedSubjects: readonly EvidenceOwnerCandidate[] = [selected]): EvidenceOwnershipCheck {
  const filename = normalize(filenameValue.replace(/\.[^.]+$/, ''));
  const selectedTokens = nameTokens(selected.name);
  const selectedMatched = selectedTokens.some((token) => filename.includes(token));
  const other = authorisedSubjects.find((subject) => subject.id !== selected.id
    && nameTokens(subject.name).some((token) => filename.includes(token)));
  if (other && !selectedMatched) return { state: 'mismatch', detectedName: other.name, detectedBy: 'known-name' };
  if (selectedMatched) return { state: 'unverified', detectedName: selected.name, detectedBy: 'source-filename' };
  const candidate = filenameOwnerCandidate(filenameValue);
  return candidate ? { state: 'mismatch', detectedName: candidate, detectedBy: 'source-filename' }
    : { state: 'confirmed' };
}

export function evaluateExtractedOwnership(analysis: import('../../lib/api').ClassificationResult['document_analysis'],
  selected: EvidenceOwnerCandidate): ExtractedOwnershipCheck | undefined {
  if (!analysis) return undefined;
  const proposal = { extractor: analysis.extractor,
    ...(analysis.owner_name ? { detectedName: analysis.owner_name } : {}),
    ...(analysis.tax_identifier ? { taxIdentifier: analysis.tax_identifier } : {}),
    ...(analysis.ownership_confidence ? { confidence: analysis.ownership_confidence } : {}),
    ...(analysis.ownership_basis ? { basis: analysis.ownership_basis } : {}) };
  if (analysis.ownership_state === 'matched') return { state: 'matched', ...proposal };
  if (analysis.ownership_state === 'mismatch') return analysis.owner_name
    ? { state: 'mismatch', ...proposal } : { state: 'unverified', ...proposal };
  if (analysis.ownership_state === 'needs_confirmation' || analysis.ownership_state === 'not_detected') {
    return { state: 'unverified', ...proposal };
  }
  if (!analysis.owner_name) return { state: 'unverified', ...proposal };
  return tokensMatch(analysis.owner_name, nameTokens(selected.name))
    ? { state: 'matched', ...proposal }
    : { state: 'mismatch', ...proposal };
}

function tokensMatch(value: string, tokens: readonly string[]): boolean {
  const normalized = normalize(value);
  return tokens.some((token) => normalized.includes(token));
}

function nameTokens(name: string): string[] {
  const normalized = normalize(name);
  const ignored = new Set(['limited', 'ltd', 'plc', 'company', 'services', 'stores', 'foods', 'enterprises',
    ...FILENAME_DESCRIPTOR_TOKENS]);
  return [normalized, ...normalized.split(' ').filter((token) => token.length >= 5 && !ignored.has(token))];
}

function normalize(value: string): string {
  return value.toLowerCase().replace(/[^a-z0-9]+/g, ' ').trim();
}

function filenameOwnerCandidate(filename: string): string | undefined {
  const stem = filename.split(/[\\/]/).at(-1)?.replace(/\.[^.]+$/, '') ?? '';
  if (/^[a-f0-9-]{16,}$/i.test(stem)) return undefined;
  const candidate: string[] = [];
  for (const token of stem.split(/[^A-Za-z]+/).filter(Boolean)) {
    const normalized = token.toLowerCase();
    if (FILENAME_DESCRIPTOR_TOKENS.has(normalized) || /^(?:19|20)\d{2}$/.test(normalized)) {
      if (candidate.length) break;
      continue;
    }
    if (token.length >= 3) candidate.push(`${token[0]!.toUpperCase()}${token.slice(1)}`);
    if (candidate.length === 4) break;
  }
  return candidate.length ? candidate.join(' ') : undefined;
}

function explicitOwnershipValues(content: string, filename: string): string[] {
  const ownerKeys = new Set(['taxpayer', 'taxpayer_name', 'client', 'client_name', 'business', 'business_name',
    'account_holder', 'account_name', 'legal_name', 'owner_name', 'registration_name']);
  if (/\.json$/i.test(filename)) {
    try {
      const value = JSON.parse(content) as Record<string, unknown>;
      return Object.entries(value).filter(([key, entry]) => ownerKeys.has(key.toLowerCase()) && typeof entry === 'string')
        .map(([, entry]) => entry as string).filter(Boolean);
    } catch { return []; }
  }
  const [headerLine, valueLine] = content.split(/\r?\n/, 2);
  if (!headerLine || !valueLine) return [];
  const headers = headerLine.split(',').map((value) => value.trim().toLowerCase());
  const values = valueLine.split(',').map((value) => value.trim().replace(/^"|"$/g, ''));
  return headers.flatMap((header, index) => ownerKeys.has(header) && values[index] ? [values[index]!] : []);
}
